import {
  GetIssueRelationshipsResponseDto,
  GetIssueResponseDto,
  useApi,
} from '@issuemanager/api-client';
import { ExtendedAxiosError } from '@issuemanager/apps/client/hooks';
import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';

type RemoveRelationshipContext = {
  previousRelationships: GetIssueRelationshipsResponseDto[] | undefined;
};

type RemoveRelationshipParams = {
  relationshipId: string;
  relatedObjectType: string;
  relatedObjectService: string;
  relatedObjectName: string;
  relatedObjectUrl?: string;
};

export function useRemoveRelationship(
  issueCode: string,
): UseMutationResult<
  GetIssueResponseDto,
  ExtendedAxiosError,
  RemoveRelationshipParams,
  RemoveRelationshipContext
> {
  const queryClient = useQueryClient();
  const api = useApi();

  return useMutation<
    GetIssueResponseDto,
    ExtendedAxiosError,
    RemoveRelationshipParams,
    RemoveRelationshipContext
  >({
    mutationKey: ['issue-relationships', issueCode, 'remove'],
    mutationFn: async ({
      relationshipId,
      relatedObjectType,
      relatedObjectService,
      relatedObjectName,
      relatedObjectUrl,
    }) => {
      const response = await api.issuesApi.deleteIssueRelationship(
        issueCode,
        relationshipId,
        {
          data: {
            relatedObjectType,
            relatedObjectService,
            relatedObjectName,
            relatedObjectUrl,
          },
        },
      );
      return response.data;
    },
    onSuccess: async (issueData) => {
      queryClient.setQueryData<GetIssueResponseDto>(
        ['issues', issueCode],
        issueData,
      );

      await queryClient.invalidateQueries({
        queryKey: ['issue-relationships', issueCode],
      });
    },
  });
}
